/*
 * Instagram feed
 * Fetches posts from the Instafeed feed endpoint, renders them into a
 * scroll-snap slider and opens them in a modal. Markup/config lives in
 * blocks/instagram-feed.liquid.
 */
(() => {
  if (customElements.get('instagram-feed')) return;

  const ENDPOINT = 'https://instafeed.nfcube.com/feed/v6';
  const MAX_POSTS = 24;
  const REQUEST_TIMEOUT = 8000;
  const CACHE_VERSION = 2;
  const CACHE_TTL = 10 * 60 * 1000; // media URLs are signed and expire, keep the cache short

  const ICONS = {
    video: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><path d="M8 5.14v13.72a1 1 0 0 0 1.52.85l11.02-6.86a1 1 0 0 0 0-1.7L9.52 4.29A1 1 0 0 0 8 5.14Z"/></svg>',
    carousel_album: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false"><rect x="3" y="7" width="14" height="14" rx="2"/><path d="M7 3h12a2 2 0 0 1 2 2v12"/></svg>',
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    prev: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M14 6L8 12L14 18"/></svg>',
    next: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M10 6L16 12L10 18"/></svg>',
    muted: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M11 5L6 9H3v6h3l5 4V5Z"/><path d="M22 9l-6 6M16 9l6 6"/></svg>',
    unmuted: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M11 5L6 9H3v6h3l5 4V5Z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13"/></svg>'
  };

  const readCache = (key) => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(key));
      if (cached && Date.now() - cached.time < CACHE_TTL) return cached.feed;
    } catch (error) {
      // storage unavailable or corrupted, fall through to the network
    }
    return null;
  };

  const writeCache = (key, feed) => {
    try {
      sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), feed }));
    } catch (error) {
      // storage full or blocked, caching is optional
    }
  };

  const mediaUrl = (media) => {
    const resolution = media && media.standard_resolution;
    return resolution && (typeof resolution === 'string' ? resolution : resolution.url);
  };

  const normalizePost = (item) => {
    const image = mediaUrl(item.images);
    if (!image || !item.link) return null;

    return {
      id: item.id,
      link: item.link,
      image,
      video: item.type === 'video' ? mediaUrl(item.videos) || null : null,
      type: item.type || 'image',
      caption: (item.caption && item.caption.text) || '',
      time: item.created_time || null
    };
  };

  const createElement = (tag, className, attributes = {}) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
    return element;
  };

  const shortCaption = (caption) => (caption.length > 120 ? `${caption.slice(0, 117)}...` : caption);

  class InstagramFeed extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.track = this.querySelector('[data-track]');
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');

      // Only hit the endpoint when the feed is close to the viewport
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        }, { rootMargin: '400px 0px' });
        this.observer.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.modal && this.modal.open) this.modal.close();
    }

    get limit() {
      const limit = parseInt(this.dataset.limit, 10) || MAX_POSTS;
      return Math.min(Math.max(limit, 1), MAX_POSTS);
    }

    get useModal() {
      return this.dataset.clickAction !== 'link' && typeof HTMLDialogElement === 'function';
    }

    label(name, fallback) {
      return this.dataset[`label${name}`] || fallback;
    }

    async load() {
      const account = this.dataset.account;
      if (!account || !this.track) return this.fail();

      const cacheKey = `instagram-feed:v${CACHE_VERSION}:${account}:${this.limit}`;
      let feed = readCache(cacheKey);

      if (!feed) {
        try {
          feed = await this.fetchFeed(account);
          writeCache(cacheKey, feed);
        } catch (error) {
          console.warn('[instagram-feed] Could not load posts', error);
          return this.fail();
        }
      }

      if (!feed.posts.length) return this.fail();

      this.posts = feed.posts;
      this.profile = feed.profile;
      this.render();
    }

    async fetchFeed(account) {
      const url = new URL(ENDPOINT);
      url.search = new URLSearchParams({
        limit: this.limit,
        account,
        fu: 0,
        fid: 0,
        locale: this.dataset.locale || 'en',
        handle: '',
        client: 'feed'
      }).toString();

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const json = await response.json();
        if (!Array.isArray(json.data)) throw new Error('Unexpected response');

        const meta = json.meta || {};
        return {
          posts: json.data.map(normalizePost).filter(Boolean).slice(0, this.limit),
          profile: {
            username: meta.username || this.dataset.username || '',
            picture: meta.profile_picture || ''
          }
        };
      } finally {
        clearTimeout(timeout);
      }
    }

    /* Slider */

    render() {
      const fragment = document.createDocumentFragment();
      const newTab = this.dataset.newTab === 'true';
      const useModal = this.useModal;

      this.posts.forEach((post, index) => {
        const item = createElement('li', 'instagram-feed__item');
        const link = createElement('a', 'instagram-feed__link', {
          href: post.link,
          'aria-label': `${this.label('Post', 'Instagram post')} ${index + 1}`
        });

        if (useModal) {
          link.setAttribute('aria-haspopup', 'dialog');
          link.addEventListener('click', (event) => {
            // Let modified clicks (new tab/window) follow the link
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
            event.preventDefault();
            this.openModal(this.posts.indexOf(post));
          });
        } else if (newTab) {
          link.target = '_blank';
          link.rel = 'noopener';
        }

        const image = createElement('img', 'instagram-feed__image', {
          src: post.image,
          alt: shortCaption(post.caption),
          loading: 'lazy',
          decoding: 'async'
        });
        image.addEventListener('error', () => {
          item.remove();
          this.posts = this.posts.filter((entry) => entry !== post);
          this.updateButtons();
        }, { once: true });
        link.appendChild(image);

        // Videos get a centered play button on hover, carousels keep the corner badge
        if (post.type === 'video') {
          const play = createElement('span', 'instagram-feed__play');
          play.innerHTML = ICONS.video;
          link.appendChild(play);
        } else if (ICONS[post.type]) {
          const icon = createElement('span', 'instagram-feed__icon');
          icon.innerHTML = ICONS[post.type];
          link.appendChild(icon);
        }

        item.appendChild(link);
        fragment.appendChild(item);
      });

      this.track.replaceChildren(fragment);
      this.track.removeAttribute('aria-busy');
      this.classList.add('instagram-feed--loaded');

      this.setupNavigation();
    }

    setupNavigation() {
      if (!this.prevButton || !this.nextButton) return;

      this.prevButton.addEventListener('click', () => this.scrollByPage(-1));
      this.nextButton.addEventListener('click', () => this.scrollByPage(1));
      this.track.addEventListener('scroll', () => this.updateButtons(), { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateButtons());
        this.resizeObserver.observe(this.track);
      }

      this.updateButtons();
    }

    get loop() {
      return this.dataset.arrowsLoop === 'true';
    }

    // Posts moved per arrow click: a full view, or the number set in the block
    postsPerClick(perPage) {
      if (this.dataset.arrowsPage === 'true') return perPage;
      return Math.max(parseInt(this.dataset.arrowsStep, 10) || 1, 1);
    }

    scrollByPage(direction) {
      const firstItem = this.track.firstElementChild;
      if (!firstItem) return;

      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const itemWidth = firstItem.getBoundingClientRect().width + gap;
      const perPage = Math.max(Math.floor((this.track.clientWidth + gap) / itemWidth), 1);
      const isRtl = getComputedStyle(this).direction === 'rtl';
      const behavior = this.reducedMotion ? 'auto' : 'smooth';

      if (this.loop) {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const position = Math.abs(this.track.scrollLeft);
        const atStart = position <= 1;
        const atEnd = position >= maxScroll - 1;

        // Wrap around to the other side of the slider
        if ((direction > 0 && atEnd) || (direction < 0 && atStart)) {
          const target = direction > 0 ? 0 : maxScroll;
          this.track.scrollTo({ left: target * (isRtl ? -1 : 1), behavior });
          return;
        }
      }

      this.track.scrollBy({
        left: direction * this.postsPerClick(perPage) * itemWidth * (isRtl ? -1 : 1),
        behavior
      });
    }

    updateButtons() {
      if (!this.prevButton || !this.nextButton) return;

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const position = Math.abs(this.track.scrollLeft);
      const scrollable = maxScroll > 1;

      this.prevButton.hidden = !scrollable;
      this.nextButton.hidden = !scrollable;
      this.prevButton.disabled = !this.loop && position <= 1;
      this.nextButton.disabled = !this.loop && position >= maxScroll - 1;
    }

    get reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /* Modal */

    buildModal() {
      const modal = createElement('dialog', 'instagram-modal', {
        'aria-label': this.label('Post', 'Instagram post')
      });

      modal.innerHTML = `
        <div class="instagram-modal__panel">
          <div class="instagram-modal__media" data-modal-media></div>
          <div class="instagram-modal__info">
            <div class="instagram-modal__header">
              <a class="instagram-modal__profile" data-modal-profile target="_blank" rel="noopener">
                <img class="instagram-modal__avatar" data-modal-avatar alt="" width="40" height="40">
                <span data-modal-username></span>
              </a>
            </div>
            <p class="instagram-modal__caption" data-modal-caption></p>
            <div class="instagram-modal__footer">
              <time data-modal-date></time>
              <a class="instagram-modal__view" data-modal-link target="_blank" rel="noopener"></a>
            </div>
          </div>
        </div>
        <button type="button" class="instagram-modal__button instagram-modal__close" data-modal-close autofocus>${ICONS.close}</button>
        <button type="button" class="instagram-modal__button instagram-modal__nav instagram-modal__nav--prev" data-modal-prev>${ICONS.prev}</button>
        <button type="button" class="instagram-modal__button instagram-modal__nav instagram-modal__nav--next" data-modal-next>${ICONS.next}</button>
      `;

      const query = (selector) => modal.querySelector(selector);
      this.modalParts = {
        media: query('[data-modal-media]'),
        profile: query('[data-modal-profile]'),
        avatar: query('[data-modal-avatar]'),
        username: query('[data-modal-username]'),
        caption: query('[data-modal-caption]'),
        date: query('[data-modal-date]'),
        link: query('[data-modal-link]'),
        prev: query('[data-modal-prev]'),
        next: query('[data-modal-next]')
      };

      query('[data-modal-close]').setAttribute('aria-label', this.label('Close', 'Close'));
      this.modalParts.prev.setAttribute('aria-label', this.label('Previous', 'Previous'));
      this.modalParts.next.setAttribute('aria-label', this.label('Next', 'Next'));
      this.modalParts.link.textContent = this.label('View', 'View on Instagram');

      query('[data-modal-close]').addEventListener('click', () => modal.close());
      this.modalParts.prev.addEventListener('click', () => this.stepModal(-1));
      this.modalParts.next.addEventListener('click', () => this.stepModal(1));

      // Clicks on the dialog itself (outside the panel) close it
      modal.addEventListener('click', (event) => {
        if (event.target === modal) modal.close();
      });

      modal.addEventListener('keydown', (event) => {
        if (event.target.closest('video')) return;
        if (event.key === 'ArrowLeft') this.stepModal(-1);
        if (event.key === 'ArrowRight') this.stepModal(1);
      });

      modal.addEventListener('close', () => {
        this.clearModalMedia();
        document.documentElement.classList.remove('instagram-modal-open');
      });

      this.appendChild(modal);
      this.modal = modal;
    }

    openModal(index) {
      if (index < 0) return;
      if (!this.modal) this.buildModal();

      this.showPost(index);

      if (!this.modal.open) {
        document.documentElement.classList.add('instagram-modal-open');
        this.modal.showModal();
      }
    }

    stepModal(direction) {
      const index = this.modalIndex + direction;
      if (index < 0 || index >= this.posts.length) return;
      this.showPost(index);
    }

    showPost(index) {
      const post = this.posts[index];
      const parts = this.modalParts;
      const username = this.profile.username || this.dataset.username || '';

      this.modalIndex = index;
      this.renderModalMedia(post);

      parts.profile.hidden = !username;
      parts.profile.href = `https://www.instagram.com/${encodeURIComponent(username)}/`;
      parts.username.textContent = username;
      parts.avatar.hidden = !this.profile.picture;
      if (this.profile.picture) parts.avatar.src = this.profile.picture;

      parts.caption.textContent = post.caption;
      parts.caption.hidden = !post.caption;

      parts.date.textContent = post.time ? this.formatDate(post.time) : '';
      if (post.time) parts.date.dateTime = new Date(post.time * 1000).toISOString();

      parts.link.href = post.link;
      parts.link.hidden = this.dataset.showViewLink !== 'true';
      parts.prev.disabled = index === 0;
      parts.next.disabled = index === this.posts.length - 1;
      this.modal.setAttribute('aria-label', `${this.label('Post', 'Instagram post')} ${index + 1}`);
    }

    renderModalMedia(post) {
      this.clearModalMedia();
      const container = this.modalParts.media;

      // Blurred copy of the thumbnail fills the space around portrait media
      const backdrop = createElement('img', 'instagram-modal__backdrop', { src: post.image, alt: '', 'aria-hidden': 'true' });
      container.appendChild(backdrop);

      if (!post.video) {
        container.appendChild(createElement('img', 'instagram-modal__image', { src: post.image, alt: shortCaption(post.caption) }));
        return;
      }

      const video = createElement('video', 'instagram-modal__video', {
        poster: post.image,
        playsinline: '',
        loop: '',
        preload: 'metadata'
      });
      video.muted = true;
      video.autoplay = !this.reducedMotion;
      video.controls = false;
      video.src = post.video;

      // Signed video URLs can expire, fall back to the still image
      video.addEventListener('error', () => {
        video.replaceWith(createElement('img', 'instagram-modal__image', { src: post.image, alt: shortCaption(post.caption) }));
        sound.remove();
      }, { once: true });

      // Clicking the video toggles playback
      video.addEventListener('click', () => (video.paused ? video.play() : video.pause()));

      const sound = createElement('button', 'instagram-modal__button instagram-modal__sound', { type: 'button' });
      const updateSound = () => {
        sound.innerHTML = video.muted ? ICONS.muted : ICONS.unmuted;
        sound.setAttribute('aria-label', video.muted ? this.label('Unmute', 'Unmute') : this.label('Mute', 'Mute'));
      };
      sound.addEventListener('click', () => {
        video.muted = !video.muted;
        if (video.paused) video.play().catch(() => {});
        updateSound();
      });
      updateSound();

      container.append(video, sound);
      if (video.autoplay) video.play().catch(() => {});
    }

    clearModalMedia() {
      if (!this.modalParts) return;
      const video = this.modalParts.media.querySelector('video');
      if (video) {
        // Stop the download as well as the playback
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
      this.modalParts.media.replaceChildren();
    }

    formatDate(seconds) {
      const date = new Date(seconds * 1000);
      const options = { month: 'long', day: 'numeric' };
      if (date.getFullYear() !== new Date().getFullYear()) options.year = 'numeric';

      try {
        return new Intl.DateTimeFormat(this.dataset.locale || undefined, options).format(date);
      } catch (error) {
        return date.toLocaleDateString();
      }
    }

    fail() {
      if (this.dataset.hideOnError === 'true') {
        this.hidden = true;
        return;
      }

      this.classList.add('instagram-feed--error');
      if (this.track) this.track.removeAttribute('aria-busy');
    }
  }

  customElements.define('instagram-feed', InstagramFeed);
})();
